import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, GripVertical, Check,
  FolderKanban, ChevronDown, ChevronRight, Info, RotateCcw,
  Layers, FileText, Package, Target, Milestone, AlertCircle, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

// Built-in WBS templates
const WBS_TEMPLATES = {
  agile: {
    name: 'Agile / Scrum',
    description: 'Theme > Epic > Feature > Story',
    levels: [
      { key: 'theme', name: 'Theme', icon: 'Layers', color: 'indigo' },
      { key: 'epic', name: 'Epic', icon: 'Package', color: 'purple' },
      { key: 'feature', name: 'Feature', icon: 'Target', color: 'blue' },
      { key: 'story', name: 'User Story', icon: 'FileText', color: 'green' }
    ]
  },
  waterfall: {
    name: 'Waterfall / Traditional',
    description: 'Phase > Workstream > Deliverable > Task',
    levels: [
      { key: 'phase', name: 'Phase', icon: 'Milestone', color: 'red' },
      { key: 'workstream', name: 'Workstream', icon: 'Layers', color: 'orange' },
      { key: 'deliverable', name: 'Deliverable', icon: 'Package', color: 'amber' },
      { key: 'task', name: 'Task', icon: 'FileText', color: 'green' }
    ]
  },
  pmo: {
    name: 'PMO / Enterprise',
    description: 'Portfolio > Program > Project > Workpackage > Activity',
    levels: [
      { key: 'portfolio', name: 'Portfolio', icon: 'Layers', color: 'slate' },
      { key: 'program', name: 'Program', icon: 'FolderKanban', color: 'indigo' },
      { key: 'project', name: 'Project', icon: 'Package', color: 'purple' },
      { key: 'workpackage', name: 'Work Package', icon: 'Target', color: 'blue' },
      { key: 'activity', name: 'Activity', icon: 'FileText', color: 'green' }
    ]
  },
  mining: {
    name: 'Mining / Resource',
    description: 'Project > Area > Discipline > Scope Item',
    levels: [
      { key: 'project', name: 'Project', icon: 'FolderKanban', color: 'amber' },
      { key: 'area', name: 'Area', icon: 'Layers', color: 'orange' },
      { key: 'discipline', name: 'Discipline', icon: 'Package', color: 'red' },
      { key: 'scope_item', name: 'Scope Item', icon: 'FileText', color: 'rose' }
    ]
  },
  simple: {
    name: 'Simple',
    description: 'Category > Item',
    levels: [
      { key: 'category', name: 'Category', icon: 'Layers', color: 'gray' },
      { key: 'item', name: 'Item', icon: 'FileText', color: 'blue' }
    ]
  }
};

// Icon options
const ICON_OPTIONS = [
  { key: 'Layers', icon: Layers, label: 'Layers' },
  { key: 'FolderKanban', icon: FolderKanban, label: 'Folder' },
  { key: 'Package', icon: Package, label: 'Package' },
  { key: 'Target', icon: Target, label: 'Target' },
  { key: 'Milestone', icon: Milestone, label: 'Milestone' },
  { key: 'FileText', icon: FileText, label: 'Document' }
];

// Color options
const COLOR_OPTIONS = [
  { key: 'gray', label: 'Gray', class: 'bg-gray-100 text-gray-700 border-gray-200' },
  { key: 'red', label: 'Red', class: 'bg-red-100 text-red-700 border-red-200' },
  { key: 'orange', label: 'Orange', class: 'bg-orange-100 text-orange-700 border-orange-200' },
  { key: 'amber', label: 'Amber', class: 'bg-amber-100 text-amber-700 border-amber-200' },
  { key: 'green', label: 'Green', class: 'bg-green-100 text-green-700 border-green-200' },
  { key: 'blue', label: 'Blue', class: 'bg-blue-100 text-blue-700 border-blue-200' },
  { key: 'indigo', label: 'Indigo', class: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { key: 'purple', label: 'Purple', class: 'bg-purple-100 text-purple-700 border-purple-200' },
  { key: 'rose', label: 'Rose', class: 'bg-rose-100 text-rose-700 border-rose-200' },
  { key: 'slate', label: 'Slate', class: 'bg-slate-100 text-slate-700 border-slate-200' }
];

const getIconComponent = (iconKey) => {
  const found = ICON_OPTIONS.find(i => i.key === iconKey);
  return found?.icon || FileText;
};

const getColorClass = (colorKey) => {
  const found = COLOR_OPTIONS.find(c => c.key === colorKey);
  return found?.class || COLOR_OPTIONS[0].class;
};

const WBSSchemaDesignerPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Schema state
  const [levels, setLevels] = useState([]);
  const [originalLevels, setOriginalLevels] = useState([]);
  const [editingLevel, setEditingLevel] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  
  // Drag state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    loadTenantConfig();
  }, [tenantId]);

  useEffect(() => {
    const changed = JSON.stringify(levels) !== JSON.stringify(originalLevels);
    setHasChanges(changed);
  }, [levels, originalLevels]);

  const loadTenantConfig = async () => {
    setLoading(true);
    try {
      const tenantResponse = await api.getTenant(tenantId);
      setTenant(tenantResponse.data);
      
      // Load existing schema or use default
      const existingSchema = tenantResponse.data.tenant?.wbs_schema;
      if (existingSchema && existingSchema.length > 0) {
        setLevels(existingSchema);
        setOriginalLevels(existingSchema);
      } else {
        // Default to Agile template
        const defaultLevels = WBS_TEMPLATES.agile.levels;
        setLevels(defaultLevels);
        setOriginalLevels(defaultLevels);
      }
    } catch (err) {
      console.error('Failed to load tenant config:', err);
      toast.error('Failed to load tenant configuration');
      navigate('/platform/tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (levels.length < 2) {
      toast.error('WBS schema must have at least 2 levels');
      return;
    }
    
    setSaving(true);
    try {
      await api.updateTenantConfig(tenantId, {
        wbs_schema: levels
      });
      
      setOriginalLevels(levels);
      toast.success('WBS schema saved successfully');
    } catch (err) {
      console.error('Failed to save schema:', err);
      toast.error('Failed to save WBS schema');
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (templateKey) => {
    const template = WBS_TEMPLATES[templateKey];
    if (template) {
      setLevels([...template.levels]);
      setShowTemplates(false);
      toast.success(`Applied "${template.name}" template`);
    }
  };

  const addLevel = () => {
    const newLevel = {
      key: `level_${Date.now()}`,
      name: 'New Level',
      icon: 'FileText',
      color: 'gray'
    };
    setLevels([...levels, newLevel]);
  };

  const removeLevel = (index) => {
    if (levels.length <= 2) {
      toast.error('WBS schema must have at least 2 levels');
      return;
    }
    setLevels(levels.filter((_, i) => i !== index));
  };

  const updateLevel = (index, field, value) => {
    const updated = [...levels];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-generate key from name if it's a new level
    if (field === 'name' && updated[index].key.startsWith('level_')) {
      updated[index].key = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    }
    
    setLevels(updated);
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (index) => {
    if (draggedIndex !== null && draggedIndex !== index) {
      const newLevels = [...levels];
      const [removed] = newLevels.splice(draggedIndex, 1);
      newLevels.splice(index, 0, removed);
      setLevels(newLevels);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const resetToOriginal = () => {
    setLevels([...originalLevels]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Platform Admin Context Banner */}
        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <Shield className="w-5 h-5 text-slate-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-700">Platform Admin</p>
            <p className="text-xs text-slate-500">
              You are configuring the WBS schema as a <strong>Platform Administrator</strong>. 
              This schema defines the work breakdown hierarchy for this tenant.
            </p>
          </div>
        </div>
        
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/platform/tenants')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
            data-testid="back-to-tenants"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tenant Directory
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">WBS Schema Designer</h1>
                <p className="text-sm text-gray-500">{tenant?.tenant?.name || 'Loading...'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {hasChanges && (
                <button
                  onClick={resetToOriginal}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid="reset-schema-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              )}
              
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  hasChanges 
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                data-testid="save-schema-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Saving...' : 'Save Schema'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Changes indicator */}
        {hasChanges && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <span className="text-sm text-yellow-700">You have unsaved changes</span>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Schema Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Templates */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div 
                className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => setShowTemplates(!showTemplates)}
              >
                <div className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-gray-500" />
                  <span className="font-medium text-gray-900">Templates</span>
                </div>
                {showTemplates ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
              
              {showTemplates && (
                <div className="border-t border-gray-100 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(WBS_TEMPLATES).map(([key, template]) => (
                      <button
                        key={key}
                        onClick={() => applyTemplate(key)}
                        className="p-3 text-left border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                        data-testid={`template-${key}`}
                      >
                        <h4 className="font-medium text-gray-900">{template.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Schema Levels */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Hierarchy Levels</h3>
                    <p className="text-sm text-gray-500">Drag to reorder. Click to edit.</p>
                  </div>
                  <button
                    onClick={addLevel}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    data-testid="add-level-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Level
                  </button>
                </div>
              </div>
              
              <div className="divide-y divide-gray-100">
                {levels.map((level, index) => {
                  const IconComponent = getIconComponent(level.icon);
                  const isEditing = editingLevel === index;
                  const isDragging = draggedIndex === index;
                  const isDragOver = dragOverIndex === index;
                  
                  return (
                    <div
                      key={level.key}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={() => handleDrop(index)}
                      onDragEnd={handleDragEnd}
                      className={`px-5 py-4 transition-colors ${
                        isDragging ? 'opacity-50 bg-gray-100' : ''
                      } ${isDragOver ? 'border-t-2 border-indigo-500' : ''}`}
                      data-testid={`level-${level.key}`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Drag Handle */}
                        <div className="cursor-grab text-gray-400 hover:text-gray-600">
                          <GripVertical className="w-5 h-5" />
                        </div>
                        
                        {/* Level Number */}
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
                          {index + 1}
                        </div>
                        
                        {/* Icon & Name */}
                        <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${getColorClass(level.color)}`}>
                          <IconComponent className="w-4 h-4" />
                          {isEditing ? (
                            <input
                              type="text"
                              value={level.name}
                              onChange={(e) => updateLevel(index, 'name', e.target.value)}
                              onBlur={() => setEditingLevel(null)}
                              onKeyDown={(e) => e.key === 'Enter' && setEditingLevel(null)}
                              autoFocus
                              className="bg-transparent border-none outline-none text-sm font-medium w-32"
                            />
                          ) : (
                            <span 
                              className="text-sm font-medium cursor-pointer"
                              onClick={() => setEditingLevel(index)}
                            >
                              {level.name}
                            </span>
                          )}
                        </div>
                        
                        {/* Key */}
                        <code className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                          {level.key}
                        </code>
                        
                        {/* Spacer */}
                        <div className="flex-1" />
                        
                        {/* Color Selector */}
                        <div className="flex items-center gap-1">
                          {COLOR_OPTIONS.slice(0, 6).map(color => (
                            <button
                              key={color.key}
                              onClick={() => updateLevel(index, 'color', color.key)}
                              className={`w-5 h-5 rounded-full border-2 transition-transform ${
                                level.color === color.key ? 'scale-125 border-gray-600' : 'border-transparent hover:scale-110'
                              } ${color.class.split(' ')[0]}`}
                              title={color.label}
                            />
                          ))}
                        </div>
                        
                        {/* Icon Selector */}
                        <select
                          value={level.icon}
                          onChange={(e) => updateLevel(index, 'icon', e.target.value)}
                          className="text-xs border border-gray-200 rounded px-2 py-1"
                        >
                          {ICON_OPTIONS.map(opt => (
                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                          ))}
                        </select>
                        
                        {/* Delete */}
                        <button
                          onClick={() => removeLevel(index)}
                          disabled={levels.length <= 2}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                          title={levels.length <= 2 ? 'Minimum 2 levels required' : 'Remove level'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Right: Preview */}
          <div className="space-y-6">
            {/* Live Preview */}
            <div className="bg-white rounded-lg border border-gray-200 sticky top-6">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Preview</h3>
                <p className="text-xs text-gray-500">How it will appear in the platform</p>
              </div>
              
              <div className="p-5">
                <div className="space-y-1">
                  {levels.map((level, index) => {
                    const IconComponent = getIconComponent(level.icon);
                    return (
                      <div
                        key={level.key}
                        style={{ paddingLeft: `${index * 16}px` }}
                        className="flex items-center gap-2 py-2"
                      >
                        <div className={`flex items-center gap-2 px-2 py-1 rounded ${getColorClass(level.color)}`}>
                          <IconComponent className="w-3 h-3" />
                          <span className="text-xs font-medium">{level.name}</span>
                        </div>
                        {index < levels.length - 1 && (
                          <span className="text-gray-300 text-xs">...</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Example hierarchy */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-3">Example breakdown:</p>
                  <div className="text-xs text-gray-600 space-y-1 font-mono bg-gray-50 p-3 rounded">
                    {levels.map((level, index) => (
                      <div key={level.key} style={{ paddingLeft: `${index * 8}px` }}>
                        {index > 0 && '└─ '}
                        {level.name} {index === 0 ? '1' : `1.${index}`}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Info Box */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Schema Impact
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>Requirements module will use this hierarchy</li>
                <li>Baseline builder will follow these levels</li>
                <li>AI scoping will suggest items per level</li>
                <li>Reports will group data by these levels</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WBSSchemaDesignerPage;
